import { HydrationBoundary, QueryClient, dehydrate } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  data,
  isRouteErrorResponse,
  redirect,
  useLoaderData,
  useRouteError,
  type HeadersFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
  type ShouldRevalidateFunction,
} from "react-router";
import EtfDetailPage from "../UI/pages/EtfDetail";
import { EtfDetailError } from "../UI/pages/EtfDetail/EtfDetailError";
import { etfKeys } from "../api/services/etf.hooks";
import { ETF_HOLDINGS_PAGE_SIZE } from "../api/services/etf.type";
import { fixedT } from "../i18n/i18n.server";
import { splitLocalePath, withLocale } from "../i18n/localePath";
import { PathGenerators } from "../router/paths";
import { buildEtfSeoTags } from "../seo/etfSeo";
import { localeAlternateMeta, seoTagsToMeta } from "../seo/routeMeta";
import { routeBreadcrumb } from "../seo/structuredData";
import { serverConfig } from "../server/config.server";
import {
  EtfNotFoundError,
  fetchEtf,
  fetchEtfHoldingsPage,
  isEtfSymbol,
} from "../server/etfData.server";

/** Seconds a page rendered without the basket may be cached. */
const DEGRADED_TTL_SECONDS = 60;

const cacheControl = (ttl: number) =>
  `public, max-age=0, s-maxage=${ttl}, stale-while-revalidate=${ttl}`;

// Negative results are never cached: a fund missing today may be admitted
// tomorrow (ETF handover §7). no-store also keeps them out of the page cache.
const NO_STORE = { "Cache-Control": "no-store" };

/**
 * Server-renders one fund: its row and the first page of its basket, so the
 * verdict, rulebooks and largest holdings are in the HTML. Further basket
 * pages load in the browser through /data/etf/:symbol/holdings.
 */
export async function loader({ params, request }: LoaderFunctionArgs) {
  const raw = params.symbol ?? "";
  const ticker = raw.toUpperCase();

  const url = new URL(request.url);
  const locale = splitLocalePath(url.pathname).locale;

  // Tickers are canonically uppercase: /etf/spus -> /etf/SPUS.
  if (raw !== ticker) {
    const target = PathGenerators.ETF(ticker);
    throw redirect(`${locale ? withLocale(target, locale) : target}${url.search}`, 301);
  }
  if (!isEtfSymbol(ticker)) throw data({ ticker }, { status: 404, headers: NO_STORE });

  const holdingsQuery = { ticker, page: 1, limit: ETF_HOLDINGS_PAGE_SIZE };
  const [fundResult, holdingsResult] = await Promise.allSettled([
    fetchEtf(ticker),
    fetchEtfHoldingsPage(holdingsQuery),
  ]);

  if (fundResult.status === "rejected") {
    if (fundResult.reason instanceof EtfNotFoundError) {
      throw data({ ticker }, { status: 404, headers: NO_STORE });
    }
    console.error("etf page SSR:", (fundResult.reason as Error).message);
    throw data({ ticker }, { status: 503, headers: NO_STORE });
  }
  const fund = fundResult.value;

  // The basket is seeded under the hook's own key; if it failed, the page
  // loads it in the browser and this render is cached only briefly.
  const queryClient = new QueryClient();
  const holdingsRendered = holdingsResult.status === "fulfilled";
  if (holdingsRendered) {
    queryClient.setQueryData(etfKeys.holdings(ticker, 1), holdingsResult.value);
  } else {
    console.error("etf holdings SSR:", (holdingsResult.reason as Error).message);
  }

  const localeT = fixedT(locale);
  const seo = buildEtfSeoTags(fund, (key, options) => localeT(key, options), locale);

  // Home → ETF Screener → "Fund name (TICKER)", only for indexable pages.
  const fundPath = PathGenerators.ETF(ticker);
  const breadcrumb =
    seo.robots === "index, follow"
      ? routeBreadcrumb(
          "ETF",
          locale ? withLocale(fundPath, locale) : fundPath,
          fund.fundName ? `${fund.fundName} (${ticker})` : ticker,
        )
      : null;

  const ttl = holdingsRendered
    ? Math.min(serverConfig.screenerCacheTtlSeconds, serverConfig.pageCacheTtlSeconds)
    : DEGRADED_TTL_SECONDS;

  return data(
    { ticker, fund, seo, breadcrumb, dehydrated: dehydrate(queryClient) },
    { headers: { "Cache-Control": cacheControl(ttl) } },
  );
}

export const headers: HeadersFunction = ({ loaderHeaders, errorHeaders }) =>
  errorHeaders ?? loaderHeaders;

export const meta: MetaFunction<typeof loader> = ({ data: loaderData, error, params }) => {
  if (error || !loaderData) {
    const ticker = (params.symbol ?? "").toUpperCase();
    return [
      { title: `${ticker} | Akinda` },
      { name: "robots", content: "noindex, follow" },
    ];
  }
  return [
    ...seoTagsToMeta(loaderData.seo),
    ...(loaderData.seo.robots === "index, follow"
      ? localeAlternateMeta(loaderData.seo.canonical)
      : []),
    ...(loaderData.breadcrumb ? [{ "script:ld+json": loaderData.breadcrumb }] : []),
  ];
};

// Paging the basket is component state; only a new fund re-runs the loader.
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}) => (currentParams.symbol !== nextParams.symbol ? defaultShouldRevalidate : false);

export default function EtfRoute() {
  const { fund, dehydrated } = useLoaderData<typeof loader>();
  return (
    <HydrationBoundary state={dehydrated}>
      <EtfDetailPage fund={fund} />
    </HydrationBoundary>
  );
}

export function ErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const ticker = isRouteErrorResponse(error)
    ? (error.data as { ticker?: string } | undefined)?.ticker
    : undefined;
  const p = "pages.etfDetail.errors.";
  return (
    <EtfDetailError
      title={notFound ? t(`${p}notFoundTitle`, { ticker: ticker ?? "" }) : t(`${p}loadFailed`)}
      text={notFound ? t(`${p}notFound`) : t(`${p}tryAgain`)}
    />
  );
}
